import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import { UserState, User, PersonalSettings, AccessKey, PayoutAccessKey } from './types';
import * as types from './mutation-types';

// 更新・削除・ロック解除のエラーをクリアする
const resetChangesAllErrors = (state: UserState): void => {
    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const unlockState = state.unlock;
    unlockState.error = false;
    unlockState.errorTitle = undefined;
    unlockState.errorDetails = undefined;

    const expireState = state.expire;
    expireState.error = false;
    expireState.errorTitle = undefined;
    expireState.errorDetails = undefined;

    const multifactorAuthState = state.multifactor_auth;
    multifactorAuthState.error = false;
    multifactorAuthState.errorTitle = undefined;
    multifactorAuthState.errorDetails = undefined;

    const showPersonalSettingsState = state.show_personal_settings;
    showPersonalSettingsState.error = false;
    showPersonalSettingsState.errorTitle = undefined;
    showPersonalSettingsState.errorDetails = undefined;

    const updatePersonalSettingsState = state.update_personal_settings;
    updatePersonalSettingsState.error = false;
    updatePersonalSettingsState.errorTitle = undefined;
    updatePersonalSettingsState.errorDetails = undefined;

    const createAccessKeyState = state.access_key_create;
    createAccessKeyState.error = false;
    createAccessKeyState.errorTitle = undefined;
    createAccessKeyState.errorDetails = undefined;

    const updateAccessKeyState = state.access_key_update;
    updateAccessKeyState.error = false;
    updateAccessKeyState.errorTitle = undefined;
    updateAccessKeyState.errorDetails = undefined;

    const deleteAccessKyeState = state.access_keys_delete;
    deleteAccessKyeState.error = false;
    deleteAccessKyeState.errorTitle = undefined;
    deleteAccessKyeState.errorDetails = undefined;
};

export const mutations: MutationTree<UserState> = {
    // ユーザー検索系
    [types.SEARCH_START](state) {
        resetChangesAllErrors(state);
        const searchState = state.search;
        searchState.error = false;
        searchState.errorCode = undefined;
        searchState.errorMsg = undefined;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; users: Array<User> }) {
        state.userCount = payload.count;
        state.users = payload.users;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // ユーザー作成系
    [types.NEW_USER](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // ユーザー詳細情報参照系
    [types.SHOW_START](state) {
        resetChangesAllErrors(state);
        const showState = state.show;
        showState.error = false;
        showState.errorCode = undefined;
        showState.errorMsg = undefined;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: { user: User; group: Array<any>; multifactor_token: Array<any> }) {
        state.userDetails.user = payload.user;
        state.userDetails.groups = payload.group.length > 0 ? payload.group : [];
        state.userDetails.multifactor_token = payload.multifactor_token;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // ユーザー更新系
    [types.UPDATE_START](state) {
        resetChangesAllErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // ユーザー削除系
    [types.DESTROY_START](state) {
        resetChangesAllErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // ロック解除系
    [types.UNLOCK_START](state) {
        resetChangesAllErrors(state);
        const unlockState = state.unlock;
        unlockState.isProcessing = true;
    },

    [types.UNLOCK_SUCCESS](state) {
        const unlockState = state.unlock;
        unlockState.isProcessing = false;
    },

    [types.UNLOCK_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const unlockState = state.unlock;
        unlockState.errorTitle = payload.errorTitle;
        unlockState.errorDetails = payload.errorDetails;
        unlockState.error = true;
        unlockState.isProcessing = false;
    },

    // ユーザー失効
    [types.EXPIRE_START](state) {
        resetChangesAllErrors(state);
        const expireState = state.expire;
        expireState.isProcessing = true;
    },

    [types.EXPIRE_SUCCESS](state) {
        const expireState = state.expire;
        expireState.isProcessing = false;
    },

    [types.EXPIRE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const expireState = state.expire;
        expireState.errorTitle = payload.errorTitle;
        expireState.errorDetails = payload.errorDetails;
        expireState.error = true;
        expireState.isProcessing = false;
    },

    // パスワード更新系
    [types.UPDATE_PASSWORD_START](state) {
        resetChangesAllErrors(state);
        const updatePasswordState = state.update_password;
        updatePasswordState.isProcessing = true;
    },

    [types.UPDATE_PASSWORD_SUCCESS](state) {
        const updatePasswordState = state.update_password;
        updatePasswordState.isProcessing = false;
    },

    [types.UPDATE_PASSWORD_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updatePasswordState = state.update_password;
        updatePasswordState.errorTitle = payload.errorTitle;
        updatePasswordState.errorDetails = payload.errorDetails;
        updatePasswordState.error = true;
        updatePasswordState.isProcessing = false;
        localStorage.removeItem('sio_auth');
    },

    [types.PASSWORD_ERROR_RESET](state) {
        const updatePasswordState = state.update_password;
        updatePasswordState.error = false;
        updatePasswordState.errorTitle = undefined;
        updatePasswordState.errorDetails = undefined;
    },

    // 多要素認証設定
    [types.MULTIFACTOR_AUTH_START](state) {
        resetChangesAllErrors(state);
        const multifactorAuthState = state.multifactor_auth;
        multifactorAuthState.isProcessing = true;
    },

    [types.MULTIFACTOR_AUTH_SUCCESS](state) {
        const multifactorAuthState = state.multifactor_auth;
        multifactorAuthState.isProcessing = false;
    },

    [types.MULTIFACTOR_AUTH_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const multifactorAuthState = state.multifactor_auth;
        multifactorAuthState.errorTitle = payload.errorTitle;
        multifactorAuthState.errorDetails = payload.errorDetails;
        multifactorAuthState.error = true;
        multifactorAuthState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetChangesAllErrors(state);
        const csvState = state.csv;
        csvState.error = false;
        csvState.errorCode = undefined;
        csvState.errorMsg = undefined;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { code: string; message: string }) {
        const csvState = state.csv;
        csvState.errorCode = payload.code;
        csvState.errorMsg = payload.message;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // 個人設定取得系
    [types.SHOW_PERSONAL_SETTINGS_OUTPUT_START](state) {
        resetChangesAllErrors(state);
        const show_personal_settingsState = state.show_personal_settings;
        show_personal_settingsState.error = false;
        show_personal_settingsState.errorTitle = undefined;
        show_personal_settingsState.errorDetails = undefined;
        show_personal_settingsState.isProcessing = true;
    },

    [types.SHOW_PERSONAL_SETTINGS_OUTPUT_SUCCESS](state, payload: PersonalSettings) {
        state.personalSettings = payload;
        const show_personal_settingsState = state.show_personal_settings;
        show_personal_settingsState.isProcessing = false;
    },

    [types.SHOW_PERSONAL_SETTINGS_OUTPUT_FAILURE](
        state,
        payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }
    ) {
        const show_personal_settingsState = state.show_personal_settings;
        show_personal_settingsState.errorTitle = payload.errorTitle;
        show_personal_settingsState.errorDetails = payload.errorDetails;
        show_personal_settingsState.error = true;
        show_personal_settingsState.isProcessing = false;
    },

    [types.ACCESS_KEYS_START](state) {
        resetChangesAllErrors(state);
        const searchState = state.access_keys;
        searchState.error = false;
        searchState.errorCode = undefined;
        searchState.errorMsg = undefined;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.ACCESS_KEYS_SUCCESS](
        state,
        payload: { count: number; default_expiration_at: string; access_keys: Array<AccessKey> }
    ) {
        state.accessKeys.default_expiration_at = payload.default_expiration_at;
        state.accessKeys.count = payload.count;
        state.accessKeys.access_keys = payload.access_keys;

        const searchState = state.access_keys;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.ACCESS_KEYS_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.access_keys;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    [types.CREATE_ACCESS_KEYS_START](state) {
        resetChangesAllErrors(state);
        const createState = state.access_key_create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isCreated = false;
    },

    [types.CREATE_ACCESS_KEYS_SUCCESS](state, payload: PayoutAccessKey) {
        state.payoutAccessKey = payload;

        const createState = state.access_key_create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_ACCESS_KEYS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.access_key_create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    [types.UPDATE_ACCESS_KEYS_START](state) {
        resetChangesAllErrors(state);
        const updateState = state.access_key_update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_ACCESS_KEYS_SUCCESS](state) {
        const updateState = state.access_key_update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_ACCESS_KEYS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.access_key_update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // アクセスキー削除系
    [types.DELETE_ACCESS_KEYS_START](state) {
        resetChangesAllErrors(state);
        const destroyState = state.access_keys_delete;
        destroyState.isProcessing = true;
    },

    [types.DELETE_ACCESS_KEYS_SUCCESS](state) {
        const destroyState = state.access_keys_delete;
        destroyState.isProcessing = false;
    },

    [types.DELETE_ACCESS_KEYS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.access_keys_delete;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // 個人設定更新系
    [types.UPDATE_PERSONAL_SETTINGS_OUTPUT_START](state) {
        resetChangesAllErrors(state);
        const update_personal_settingsState = state.update_personal_settings;
        update_personal_settingsState.error = false;
        update_personal_settingsState.errorTitle = undefined;
        update_personal_settingsState.errorDetails = undefined;
        update_personal_settingsState.isProcessing = true;
    },

    [types.UPDATE_PERSONAL_SETTINGS_OUTPUT_SUCCESS](state) {
        const update_personal_settingsState = state.update_personal_settings;
        update_personal_settingsState.isProcessing = false;
    },

    [types.UPDATE_PERSONAL_SETTINGS_OUTPUT_FAILURE](
        state,
        payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }
    ) {
        const update_personal_settingsState = state.update_personal_settings;
        update_personal_settingsState.errorTitle = payload.errorTitle;
        update_personal_settingsState.errorDetails = payload.errorDetails;
        update_personal_settingsState.error = true;
        update_personal_settingsState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const unlockState = state.unlock;
        unlockState.isProcessing = false;

        const expireState = state.expire;
        expireState.isProcessing = false;

        const updatePasswordState = state.update_password;
        updatePasswordState.isProcessing = false;

        const multifactorAuthState = state.multifactor_auth;
        multifactorAuthState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;

        const showPersonalSettingsState = state.show_personal_settings;
        showPersonalSettingsState.isProcessing = false;

        const updatePersonalSettingsState = state.update_personal_settings;
        updatePersonalSettingsState.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        resetChangesAllErrors(state);

        state.users = undefined;
        state.userDetails.user = undefined;
        state.userDetails.groups = undefined;
        state.userDetails.multifactor_token = undefined;
        state.userCount = 0;
        state.personalSettings = undefined;
    },
};
